// components/KeepCopySwitch.jsx — Anamoria SPA
// v1.2 — Consent copy/UX tweak (Oct 4, 2026): feed box 14px; OFF + ON both right-aligned (box left of label, tight gap); ON reordered box-then-label to match OFF; KC-6 off-note reworded to the feature's own verb "keep a copy" (copy v0.4).
// v1.1 — Consent copy tweaks (Oct 4, 2026): no "Keeping a copy" header, no border/box, checkbox
//   right-aligned on the row, label expanded to "Let {owner} keep a copy of the memories I share".
// v1.0 — Consent PR E3a (Oct 4, 2026; spec PR-E v0.2 §2 E3; plan v0.26 §8 D9a; copy v0.3 §3.3).
//   The contributor's per-space "keep a copy" switch, in their view of the space.
//   - Rendered only when space.keepCopyCaptureEnabled (server-resolved; dark otherwise).
//   - OFF today → the shared ConsentCheckbox for contributor_keep_copy (KC-4 label): it fetches the stored
//     notice and hides itself on 404, so turning ON is impossible until the wording exists; ticking sends
//     PATCH /me/contributions/{spaceId} { keepCopy: true, keepCopyShownHash } (a grant carries the hash — B2).
//   - ON today → a plain checked switch; unticking sends { keepCopy: false } (withdrawal needs no notice —
//     ICO: as easy to withdraw as to give) and then shows KC-6.
//   - KC-7 on any failure; the switch returns to its last saved state. onChange(allowed) tells the page.
import { useState } from 'react';
import ConsentCheckbox from './ConsentCheckbox';

export default function KeepCopySwitch({ space, api, prefix, spaceId, onChange }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);   // 'off' (KC-6) | 'error' (KC-7) | null
  if (!space?.keepCopyCaptureEnabled || !api) return null;
  const allowed = space.keepCopyAllowed === true;
  const owner = space.ownerName || 'the space owner';

  async function save(keepCopy, shownHash) {
    setBusy(true); setNotice(null);
    try {
      await api.patch(`${prefix}/${spaceId}`, keepCopy ? { keepCopy: true, keepCopyShownHash: shownHash } : { keepCopy: false });
      onChange?.(keepCopy);
      if (!keepCopy) setNotice('off');
    } catch {
      setNotice('error');
    } finally { setBusy(false); }
  }

  return (
    <section style={S.wrap} aria-label="Keeping a copy">
      {allowed ? (
        <label htmlFor="keep-copy-switch" style={S.row}>
          <input id="keep-copy-switch" type="checkbox" role="switch" checked disabled={busy} onChange={() => save(false)} style={S.box} />
          <span style={S.label}>Let {owner} keep a copy</span>
        </label>
      ) : (
        <ConsentCheckbox
          api={api}
          docType="contributor_keep_copy"
          id="keep-copy-switch"
          boxOnRight
          ownerName={owner}
          label={`Let ${owner} keep a copy`}
          checked={false}
          disabled={busy}
          onChange={(checked, hash) => { if (checked) save(true, hash); }}
        />
      )}
      {notice === 'off' && <p style={S.note} role="status">From now on, {owner} won&apos;t be able to keep a copy of your memories. Anything they&apos;ve already saved stays with them.</p>}
      {notice === 'error' && <p style={S.error} role="alert">We weren&apos;t able to save that choice. Your memories are safe. When you&apos;re ready, please try again.</p>}
    </section>
  );
}

const S = {
  wrap: { margin: '0 16px 0' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, cursor: 'pointer', minHeight: 24 },
  box: { width: 14, height: 14, margin: 0, flex: '0 0 auto', accentColor: 'var(--color-accent, #5b7f6a)' },
  label: { fontSize: 15, lineHeight: 1.45, color: 'var(--color-text-primary, #2d3436)' },
  note: { margin: '8px 0 0', fontSize: 13, color: '#6b6459', textAlign: 'center' },
  error: { margin: '8px 0 0', fontSize: 13, color: '#8a3b2e' },
};
